import {
  TeCallout,
  TeContainer,
  TeContainerSlim,
  TeForm,
  TeIcon,
  TeInputText,
  TeLabel,
  TePopover,
  TeText,
  TeToggle,
} from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import { countries } from '@topcon/topcon-utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { TeToggleEvent } from '@topcon/tuicl';

import SelectableItem from '../SelectableItem';
import {
  AddressSuggestionItem,
  RequestBusinessPartnerEndCustomerTypeEnum,
} from '../../api/generated/pm';
import {
  isProxyFormData,
  providerCustomerNumberMaxLength,
  ProxyBusinessPartnerFormData,
  RequestBusinessPartnerFormData,
  requestBusinessPartnerFormSchema,
  RequestBusinessPartnerFormType,
} from '../../hooks/forms/useBusinessPartnerForm';
import SchemaValidatedForm from '../SchemaValidatedForm';
import { Country, CountryDropdown } from '../CountryDropdown';
import SearchCompany, { SearchCompanyRef } from '../SearchCompany';
import { DEFAULT_COUNTRY } from '../../app/constants';
import usePopoverController from './PopoverController/usePopoverController';
import GeolocationCallout from '../common/GeolocationCallout/GeolocationCallout';
import { useGeolocation } from '../../hooks/useGeolocation';

function IconTextRow({ icon, text }: { readonly icon: string; readonly text: string }) {
  return (
    <TeContainerSlim gap="xs" wrap>
      <TeIcon name={icon} />
      <TeText size={12} color="text.secondary">
        {text}
      </TeText>
    </TeContainerSlim>
  );
}

interface BusinessPartnerFormProps {
  businessPartnerType: RequestBusinessPartnerFormType;
  formName: string;
  isPending: boolean;
  handleFormSubmit: () => void;
  formFields: RequestBusinessPartnerFormData | ProxyBusinessPartnerFormData;
  setFormFields: (fields: RequestBusinessPartnerFormData | ProxyBusinessPartnerFormData) => void;
  /** Gates the `SearchCompany` company-name typeahead and its popover. */
  useLocationSuggestion?: boolean;
  /**
   * Gates geolocation auto-detect, `GeolocationCallout`, and the country
   * dropdown's loading spinner — independently of `useLocationSuggestion`.
   * Defaults to whatever `useLocationSuggestion` is, so every existing
   * caller is unchanged — including the ones that pass neither prop to
   * opt out of geolocation entirely (e.g. `EditBusinessPartnerModal`).
   * Callers that seed this form from a source system's own address (e.g. a
   * Migration Candidate) should pass `false` explicitly even when
   * `useLocationSuggestion` is `true`: that data already carries a country,
   * and guessing one from the browser's location would contradict it.
   */
  detectCountry?: boolean;
  resetFieldsStatus?: boolean;
  /** Renders a dismissible "please verify" banner at the top of the General Information section. */
  showVerifyBanner?: boolean;
  /** Called when the verify banner is dismissed. The parent owns the dismissed state so it survives step navigation. */
  onDismissVerifyBanner?: () => void;
  /** When true, address fields are always editable and the manual-input toggle is hidden. */
  alwaysEditableAddress?: boolean;
}

function BusinessPartnerForm({
  businessPartnerType,
  formName,
  isPending,
  handleFormSubmit,
  formFields,
  setFormFields,
  useLocationSuggestion,
  detectCountry = useLocationSuggestion,
  resetFieldsStatus,
  showVerifyBanner,
  onDismissVerifyBanner,
  alwaysEditableAddress,
}: BusinessPartnerFormProps) {
  const { t } = useTranslation(['companies', 'divisions', 'common']);
  const {
    isLoading: isReverseGeocodingLoading,
    country: geolocatedCountry,
    startCountryDetection,
  } = useGeolocation();
  const searchCompanyRef = useRef<SearchCompanyRef>(null);
  const country = countries.getCountry(formFields.country);
  const isDivision = businessPartnerType === 'division';
  const popkey = `search-company-${formName}`;
  const [manualInputEnabled, setManualInputEnabled] = useState<boolean>(false);
  const [showGeolocationCallout, setShowGeolocationCallout] = useState<boolean>(true);
  const addressReadonly = alwaysEditableAddress ? isPending : !manualInputEnabled || isPending;
  const { popoverOpen, popoverLabel, showPopover } = usePopoverController({
    manualInputEnabled,
    countryName: country?.name,
  });
  const appliedCountryRef = useRef<string | null>(null);
  const hasUserInteractedRef = useRef<boolean>(false);
  // Captured once, on first render: did this form mount already carrying an
  // address? If so, it was seeded from a source system (a Migration
  // Candidate, a Customer Suggestion) and a geolocated country must never
  // wipe it — the prefill arrives via `defaultValues`, not a user edit, so
  // `hasUserInteractedRef` alone cannot guard against it.
  const seededWithAddressRef = useRef<boolean>(Boolean(formFields.addressLineOne));

  const handleSetFormFields = useCallback(
    (fields: RequestBusinessPartnerFormData | ProxyBusinessPartnerFormData) => {
      hasUserInteractedRef.current = true;
      setFormFields(fields);
    },
    [setFormFields]
  );

  const onSelectCompany = useCallback(
    (company: AddressSuggestionItem | string) => {
      if (typeof company === 'string') {
        handleSetFormFields({ ...formFields, name: company });
        return;
      }
      // Suggestion fields are nullable; the form holds plain strings.
      handleSetFormFields({
        ...formFields,
        name: company.name ?? '',
        addressLineOne: company.addressLineOne ?? '',
        addressLineTwo: company.addressLineTwo || undefined,
        city: company.city ?? '',
        country: company.country ?? '',
        stateProvinceRegion: company.stateProvinceRegion ?? '',
        zipPostalCode: company.zipPostalCode ?? '',
      });
    },
    [formFields, handleSetFormFields]
  );

  const resetFormOnCountryChange = useCallback(
    (countryCode: string) => {
      searchCompanyRef.current?.resetSearch();

      setFormFields({
        ...formFields,
        addressLineOne: '',
        addressLineTwo: undefined,
        zipPostalCode: '',
        city: '',
        stateProvinceRegion: '',
        country: countryCode,
      });
    },
    [formFields, setFormFields]
  );

  const onSelectCountry = useCallback(
    (c?: Country) => {
      // On the first user country change, hide the geolocation callout
      setShowGeolocationCallout(false);
      hasUserInteractedRef.current = true;
      const country = c?.code ?? DEFAULT_COUNTRY.code;

      const shouldShowPopover = Boolean(formFields.country) && Boolean(formFields.name);

      if (shouldShowPopover) {
        showPopover();
      }

      if (manualInputEnabled) {
        handleSetFormFields({ ...formFields, country });
      } else {
        resetFormOnCountryChange(country);
      }
    },
    [
      formFields,
      handleSetFormFields,
      showPopover,
      resetFormOnCountryChange,
      manualInputEnabled,
      setShowGeolocationCallout,
    ]
  );

  // Start country detection when form mounts if geolocation detection is enabled
  useEffect(() => {
    if (detectCountry) {
      startCountryDetection();
    }
  }, [detectCountry, startCountryDetection]);

  useEffect(() => {
    if (!detectCountry) {
      return;
    }
    if (seededWithAddressRef.current) {
      return;
    }
    if (hasUserInteractedRef.current) {
      return;
    }
    if (isReverseGeocodingLoading) {
      searchCompanyRef.current?.resetSearch();
      return;
    }
    const countryCode = geolocatedCountry?.code;
    if (countryCode && appliedCountryRef.current !== countryCode) {
      appliedCountryRef.current = countryCode;
      resetFormOnCountryChange(countryCode);
    }
  }, [detectCountry, isReverseGeocodingLoading, geolocatedCountry?.code, resetFormOnCountryChange]);

  return (
    <TeForm name={formName} tesubmit={handleFormSubmit} data-testid="modal-step-1">
      <TeContainerSlim column fill gap="s">
        {businessPartnerType !== 'division' && (
          <TeContainerSlim fill gap="xs" alignItems="center" justifyContent="space-between">
            <TeContainer
              grow
              column
              padding={['l', 'm']}
              gap="l"
              justifyContent="space-between"
              backgroundColor="background.secondary"
              borderRadius={8}
            >
              <TeContainerSlim column gap="xs">
                <TeText size={16} weight={700}>
                  {t('companyType')}
                </TeText>
                <TeText size={12} color="text.secondary">
                  {t('companyTypeDescription')}
                </TeText>
              </TeContainerSlim>
              <TeContainerSlim grow gap="s">
                <SelectableItem
                  icon="business_center"
                  onClick={() =>
                    !isPending &&
                    setFormFields({
                      ...formFields,
                      type: RequestBusinessPartnerEndCustomerTypeEnum.END_CUSTOMER_INDIVIDUAL,
                    })
                  }
                  active={
                    formFields.type ===
                    RequestBusinessPartnerEndCustomerTypeEnum.END_CUSTOMER_INDIVIDUAL
                  }
                  title={t('type.small.title')}
                >
                  <TeContainerSlim column gap="xs">
                    <IconTextRow icon="check" text={t('type.small.description1')} />
                    <IconTextRow icon="check" text={t('type.small.description2')} />
                  </TeContainerSlim>
                </SelectableItem>

                <SelectableItem
                  icon="person"
                  onClick={() =>
                    !isPending &&
                    setFormFields({
                      ...formFields,
                      type: RequestBusinessPartnerEndCustomerTypeEnum.END_CUSTOMER_ENTERPRISE,
                    })
                  }
                  active={
                    formFields.type ===
                    RequestBusinessPartnerEndCustomerTypeEnum.END_CUSTOMER_ENTERPRISE
                  }
                  title={t('type.large.title')}
                >
                  <TeContainerSlim column gap="xs">
                    <IconTextRow icon="check" text={t('type.large.description1')} />
                    <IconTextRow icon="check" text={t('type.large.description2')} />
                    <IconTextRow icon="check" text={t('type.large.description3')} />
                  </TeContainerSlim>
                </SelectableItem>
              </TeContainerSlim>
            </TeContainer>
          </TeContainerSlim>
        )}
        <TeContainerSlim fill gap="xs" alignItems="center" justifyContent="space-between">
          <TeContainer
            column
            grow
            padding={['l', 'm']}
            gap="l"
            justifyContent="space-between"
            backgroundColor="background.secondary"
            borderRadius={8}
          >
            {showVerifyBanner && (
              <TeCallout
                status="info"
                heading={t('verifyBanner.heading')}
                text={t('verifyBanner.text')}
                closeable
                teclose={onDismissVerifyBanner}
                grow
              />
            )}

            <TeContainerSlim column gap="xs">
              <TeText size={16} weight={700}>
                {isDivision ? t('divisions', { ns: 'divisions' }) : t('generalInformation')}
              </TeText>
              <TeText size={12} color="text.secondary">
                {isDivision
                  ? t('noDivisionsDescription', { ns: 'divisions' })
                  : t('generalInformationDescription')}
              </TeText>
            </TeContainerSlim>

            {detectCountry && showGeolocationCallout && <GeolocationCallout />}

            <TeContainerSlim column grow gap="m">
              <TeContainerSlim column grow gap="xs">
                <TeContainerSlim column fill gap="xs">
                  <TeLabel text={t('country', { ns: 'common' })} />
                  <CountryDropdown
                    selectedCountry={formFields.country}
                    onSelectCountry={onSelectCountry}
                    disabled={isPending}
                    loading={detectCountry && isReverseGeocodingLoading}
                  />
                </TeContainerSlim>
                {useLocationSuggestion ? (
                  <>
                    <TeContainerSlim
                      fill
                      gap="xs"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <TeLabel
                        text={
                          isDivision
                            ? t('divisionName', { ns: 'divisions' })
                            : t('companyName', { ns: 'common' })
                        }
                      />
                      {!alwaysEditableAddress && (
                        <TeToggle
                          size="s"
                          toggled={manualInputEnabled}
                          tetoggle={(e: TeToggleEvent) => {
                            hasUserInteractedRef.current = true;
                            setManualInputEnabled(e.toggled);
                          }}
                          label={t('manuallyInputInformation', {
                            ns: isDivision ? 'divisions' : 'common',
                          })}
                          disabled={isPending}
                          labelPlacement="left"
                          labelWeight={400}
                        />
                      )}
                    </TeContainerSlim>
                    <SearchCompany
                      ref={searchCompanyRef}
                      defaultCompanyName={formFields.name}
                      countryCode={formFields.country}
                      placeholder={
                        isDivision
                          ? t('searchPlaceholder', { ns: 'divisions' })
                          : t('companyNamePlaceholder', { ns: 'common' })
                      }
                      disabled={isPending}
                      popkey={popkey}
                      onSelectCompany={onSelectCompany}
                    />
                    <TePopover for={popkey} variant="secondary" open={popoverOpen}>
                      <TeContainerSlim padding="xs">
                        <TeText color="text.inverse" size={12}>
                          {popoverLabel}
                        </TeText>
                      </TeContainerSlim>
                    </TePopover>
                  </>
                ) : (
                  <>
                    <TeContainerSlim
                      fill
                      gap="xs"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <TeLabel
                        text={
                          isDivision
                            ? t('divisionName', { ns: 'divisions' })
                            : t('name', { ns: 'common' })
                        }
                      />
                      {!alwaysEditableAddress && (
                        <TeToggle
                          size="s"
                          toggled={manualInputEnabled}
                          tetoggle={(e: TeToggleEvent) => {
                            hasUserInteractedRef.current = true;
                            setManualInputEnabled(e.toggled);
                          }}
                          label={t('manuallyInputInformation', {
                            ns: isDivision ? 'divisions' : 'common',
                          })}
                          disabled={isPending}
                        />
                      )}
                    </TeContainerSlim>
                    <TeInputText
                      size="s"
                      name="name"
                      grow
                      placeholder={
                        isDivision
                          ? t('searchPlaceholder', { ns: 'divisions' })
                          : t('namePlaceholder', { ns: 'common' })
                      }
                      value={formFields.name}
                      techange={(e) => {
                        const { target } = e;
                        const { value } = target;
                        handleSetFormFields({ ...formFields, name: value });
                      }}
                      readonly={isPending}
                    />
                  </>
                )}
              </TeContainerSlim>

              <TeContainerSlim column grow gap="xs">
                <TeLabel text={t('officeAddress', { ns: 'common' })} />
                <SchemaValidatedForm
                  schema={requestBusinessPartnerFormSchema}
                  formData={formFields}
                  setFormFields={handleSetFormFields}
                  readonly={addressReadonly}
                  resetFieldsStatus={resetFieldsStatus}
                >
                  {({ renderField }) => (
                    <TeContainerSlim column grow gap="m">
                      <TeContainerSlim column fill gap="xs">
                        {renderField(
                          'addressLineOne',
                          t('officeAddressPlaceholder', { ns: 'common' })
                        )}
                        {renderField(
                          'addressLineTwo',
                          t('officeAddress2Placeholder', { ns: 'common' })
                        )}
                      </TeContainerSlim>
                      <TeContainerSlim grow gap="s">
                        {renderField(
                          'zipPostalCode',
                          t('postalCodePlaceholder', { ns: 'common' }),
                          t('postalCode', { ns: 'common' })
                        )}
                        {renderField(
                          'city',
                          t('cityPlaceholder', { ns: 'common' }),
                          t('city', { ns: 'common' })
                        )}
                      </TeContainerSlim>
                      <TeContainerSlim grow gap="s">
                        {renderField(
                          'stateProvinceRegion',
                          t('regionPlaceholder', { ns: 'common' }),
                          t('region', { ns: 'common' })
                        )}
                      </TeContainerSlim>
                    </TeContainerSlim>
                  )}
                </SchemaValidatedForm>
              </TeContainerSlim>
            </TeContainerSlim>
          </TeContainer>
        </TeContainerSlim>
        {isProxyFormData(formFields) && (
          <TeContainer
            column
            grow
            fill
            padding={['l', 'm']}
            gap="l"
            justifyContent="space-between"
            backgroundColor="background.secondary"
            borderRadius={8}
          >
            <TeContainerSlim column gap="xs">
              <TeText size={16} weight={700}>
                {t('contactInformation', { ns: 'common' })}
              </TeText>
              <TeText size={12} color="text.secondary">
                {t('contactInformationDescription', { ns: 'common' })}
              </TeText>
            </TeContainerSlim>

            <TeContainerSlim column grow gap="m">
              <TeContainerSlim column grow gap="xs">
                <TeLabel text={t('email', { ns: 'common' })} />
                <TeInputText
                  size="s"
                  name="email"
                  grow
                  placeholder={t('emailPlaceholder', { ns: 'common' })}
                  value={formFields.email}
                  techange={(e) => {
                    const { target } = e;
                    const { value } = target;
                    setFormFields({ ...formFields, email: value });
                  }}
                />
              </TeContainerSlim>
            </TeContainerSlim>
          </TeContainer>
        )}
        {isProxyFormData(formFields) && (
          <TeContainer
            column
            grow
            fill
            padding={['l', 'm']}
            gap="l"
            justifyContent="space-between"
            backgroundColor="background.secondary"
            borderRadius={8}
          >
            <TeContainerSlim column gap="xs">
              <TeText size={16} weight={700}>
                {t('customerNumber.title', { ns: 'common' })}
              </TeText>
              <TeText size={12} color="text.secondary">
                {t('customerNumber.description', { ns: 'common' })}
              </TeText>
            </TeContainerSlim>

            <TeContainerSlim column grow gap="xs">
              <TeLabel text={t('customerNumber.label', { ns: 'common' })} />
              <TeInputText
                size="s"
                name="providerCustomerNumber"
                maxLength={providerCustomerNumberMaxLength}
                grow
                placeholder={t('customerNumber.placeholder', { ns: 'common' })}
                value={formFields.providerCustomerNumber ?? ''}
                techange={(e) => {
                  const { target } = e;
                  const { value } = target;
                  setFormFields({ ...formFields, providerCustomerNumber: value });
                }}
              />
            </TeContainerSlim>
          </TeContainer>
        )}
      </TeContainerSlim>
    </TeForm>
  );
}

export { BusinessPartnerForm };
